import React, { useEffect, useState, useRef } from 'react';
import styled, { keyframes } from 'styled-components';

const Card = styled.div`
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 12px;
  padding: 20px;
`;

const Title = styled.h3`
  color: #a78bfa;
  font-size: 14px;
  font-weight: 700;
  margin: 0 0 16px 0;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid #333;
  border-top-color: #8b5cf6;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  margin: 0 auto 12px;
`;

const LoadingMsg = styled.div`
  text-align: center;
  color: #9ca3af;
  font-size: 14px;
`;

const TextBody = styled.div`
  color: #e5e5e5;
  font-size: 15px;
  line-height: 1.8;
  white-space: pre-wrap;
`;

const ErrorMsg = styled.div`
  color: #f87171;
  font-size: 14px;
  margin-bottom: 12px;
`;

const RetryBtn = styled.button`
  padding: 8px 18px;
  background: #374151;
  border: none;
  border-radius: 8px;
  color: #e5e5e5;
  font-size: 13px;
  cursor: pointer;
  &:hover { background: #4b5563; }
`;

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

const Cursor = styled.span`
  display: inline-block;
  width: 2px;
  height: 1em;
  background: #8b5cf6;
  margin-left: 2px;
  vertical-align: text-bottom;
  animation: ${blink} 0.8s step-end infinite;
`;

const SectionWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Section = styled.div`
  background: ${({ $bg }) => $bg || '#1f1f1f'};
  border-left: 3px solid ${({ $accent }) => $accent || '#555'};
  border-radius: 0 8px 8px 0;
  padding: 10px 14px;
`;

const SectionLabel = styled.div`
  color: ${({ $color }) => $color || '#9ca3af'};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  margin-bottom: 6px;
  text-transform: uppercase;
`;

const SectionText = styled.div`
  color: #e5e5e5;
  font-size: 14px;
  line-height: 1.75;
  white-space: pre-wrap;
`;

const SECTIONS = [
  { key: '상황 읽기', label: '상황 읽기', accent: '#6b7280', color: '#9ca3af', bg: '#1a1a1a' },
  { key: '조언', label: '조언', accent: '#7c3aed', color: '#a78bfa', bg: '#1a1030' },
  { key: '주의', label: '주의', accent: '#d97706', color: '#fbbf24', bg: '#1c1700' },
];

function parseV2Sections(text) {
  const result = {};
  let remaining = text;

  for (const sec of SECTIONS) {
    const open = `【${sec.key}】`;
    const idx = remaining.indexOf(open);
    if (idx === -1) continue;
    const afterOpen = remaining.slice(idx + open.length);
    // find next 【 or end
    const nextBracket = afterOpen.search(/【/);
    const content = nextBracket === -1
      ? afterOpen.trim()
      : afterOpen.slice(0, nextBracket).trim();
    result[sec.key] = content;
    remaining = remaining.slice(0, idx) + remaining.slice(idx + open.length + (nextBracket === -1 ? afterOpen.length : nextBracket));
  }

  return result;
}

function SectionedView({ text, typing }) {
  const parsed = parseV2Sections(text);
  const hasAnySection = SECTIONS.some(s => parsed[s.key]);

  if (!hasAnySection) {
    return (
      <TextBody>
        {text}
        {typing && <Cursor />}
      </TextBody>
    );
  }

  return (
    <SectionWrap>
      {SECTIONS.map((sec, i) => {
        const content = parsed[sec.key];
        if (!content) return null;
        const isLast = i === SECTIONS.length - 1;
        return (
          <Section key={sec.key} $bg={sec.bg} $accent={sec.accent}>
            <SectionLabel $color={sec.color}>{sec.label}</SectionLabel>
            <SectionText>
              {content}
              {typing && isLast && <Cursor />}
            </SectionText>
          </Section>
        );
      })}
    </SectionWrap>
  );
}

export default function InterpretationView({ isLoading, text, error, onRetry }) {
  const [displayed, setDisplayed] = useState('');
  const [typing, setTyping] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!text) { setDisplayed(''); return; }
    setTyping(true);
    setDisplayed('');
    let i = 0;
    timerRef.current = setInterval(() => {
      i++;
      setDisplayed(text.slice(0, i));
      if (i >= text.length) {
        clearInterval(timerRef.current);
        setTyping(false);
      }
    }, 18);
    return () => clearInterval(timerRef.current);
  }, [text]);

  const isV2 = text && text.includes('【상황 읽기】');

  return (
    <Card>
      <Title>Step 5 — AI 해석</Title>

      {isLoading && (
        <>
          <Spinner />
          <LoadingMsg>주역의 뜻을 읽는 중...</LoadingMsg>
        </>
      )}

      {error && !isLoading && (
        <>
          <ErrorMsg>{error}</ErrorMsg>
          <RetryBtn onClick={onRetry}>다시 시도</RetryBtn>
        </>
      )}

      {!isLoading && !error && displayed && (
        isV2
          ? <SectionedView text={displayed} typing={typing} />
          : <TextBody>{displayed}{typing && <Cursor />}</TextBody>
      )}
    </Card>
  );
}
